import { AvatarCropModal } from '@/components/profile/AvatarCropModal';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { UserAvatar } from '@/components/ui/UserAvatar';
import { apiFetch, apiUpload, getErrorMessage } from '@/lib/api';
import type { User } from '@/types/api';
import { Camera, ImageIcon, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

type ProfileAvatarEditorProps = {
  user: User;
  onUpdated: () => Promise<void>;
};

function isImageFile(file: File): boolean {
  if (file.type.startsWith('image/')) return true;
  return /\.(jpe?g|png|gif|webp|bmp)$/i.test(file.name);
}

export function ProfileAvatarEditor({ user, onUpdated }: ProfileAvatarEditorProps) {
  const galleryInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const previewUrlRef = useRef<string | null>(null);
  const [previewSrc, setPreviewSrc] = useState<string | null>(null);
  const [cropOpen, setCropOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  function revokePreviewUrl() {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
  }

  useEffect(() => () => revokePreviewUrl(), []);

  function closeCrop() {
    if (loading) return;
    setCropOpen(false);
    setPreviewSrc(null);
    revokePreviewUrl();
  }

  function openCropWithFile(file: File) {
    setError('');
    setMessage('');
    revokePreviewUrl();

    const url = URL.createObjectURL(file);
    previewUrlRef.current = url;
    setPreviewSrc(url);
    setCropOpen(true);
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!isImageFile(file)) {
      setError('Selecione um arquivo de imagem (JPG, PNG ou WebP).');
      return;
    }
    openCropWithFile(file);
  }

  async function handleConfirmCrop(file: File) {
    setLoading(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('avatar', file);
      await apiUpload<{ user: User }>('/auth/profile/avatar', formData);
      await onUpdated();
      setMessage('Foto de perfil atualizada.');
      setCropOpen(false);
      setPreviewSrc(null);
      revokePreviewUrl();
    } catch (err) {
      setError(getErrorMessage(err, 'Erro ao enviar a foto.'));
    } finally {
      setLoading(false);
    }
  }

  async function handleRemove() {
    setLoading(true);
    setError('');
    setMessage('');
    try {
      await apiFetch('/auth/profile/avatar', { method: 'DELETE' });
      await onUpdated();
      setMessage('Foto de perfil removida.');
    } catch (err) {
      setError(getErrorMessage(err, 'Erro ao remover a foto.'));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="space-y-4">
      {message && <Alert variant="success">{message}</Alert>}
      {error && <Alert variant="error">{error}</Alert>}

      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <UserAvatar user={user} size="lg" />

        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => galleryInputRef.current?.click()}
            disabled={loading}
          >
            <ImageIcon className="h-4 w-4" />
            Escolher da galeria
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="lg:hidden"
            onClick={() => cameraInputRef.current?.click()}
            disabled={loading}
          >
            <Camera className="h-4 w-4" />
            Tirar foto
          </Button>
          {user.avatar_url && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleRemove}
              loading={loading}
            >
              <Trash2 className="h-4 w-4" />
              Remover
            </Button>
          )}
        </div>
      </div>

      <input
        ref={galleryInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        className="hidden"
        onChange={handleFileChange}
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="user"
        className="hidden"
        onChange={handleFileChange}
      />

      <AvatarCropModal
        open={cropOpen}
        imageSrc={previewSrc}
        onClose={closeCrop}
        onConfirm={handleConfirmCrop}
        loading={loading}
      />
    </div>
  );
}
